import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Formik, Form as FormBase } from 'formik';
import { dateFormatter } from '@figure/frontlib-util';
import { useDispatch } from 'react-redux';
import {
  Switch,
  Route,
  useHistory,
  useParams,
  useRouteMatch,
  generatePath,
} from 'react-router-dom';
import { useRollbar } from '@rollbar/react';
import {
  useIdentity,
  usePersonalLoan,
  useProfile,
  useSmsConsent,
  useTelemetry,
  useRedux,
  usePersonalLoanRouteFsm,
  useRedirect,
} from 'shared/hooks';
import { isUuid } from 'shared/utils';
import * as Consent from 'shared/constants/consent';
import { UNKNOWN } from 'shared/constants/uw-decision';
import { KRANG_COMPLETE } from 'shared/constants/personal-loans-progress';
import { affiliateActions, waitlistActions } from '@figure/frontlib-onboarding-redux';
import { SPACINGS } from '@figure/figure-frontlib-gui';
import { isFigure } from 'shared/utils/ThemeHelper';
import { Step } from './Step';
import { useBasicInfoAndCreateAccountSteps } from './use-basic-info-and-create-account-steps';

const Form = styled(FormBase)`
  padding: ${SPACINGS.S}px;
`;

const useSendToWaitList = () => {
  const dispatch = useDispatch();

  const sendToWaitList = async ({ name, email }) => {
    await dispatch(
      waitlistActions.saveWaitlistProduct({
        firstName: name.firstName,
        lastName: name.lastName,
        email,
        productType: 'PL',
      })
    );
    window.location.href = '/?waitlist=true';
  };

  return { sendToWaitList };
};

const useRedirectToFirstStepOnPageLoad = ({ steps }) => {
  const history = useHistory();
  const match = useRouteMatch();
  const { uuid: appUuid } = useParams();

  useEffect(() => {
    history.push(generatePath(`${match.path}/${steps[0].path}`, { uuid: appUuid }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// Is user is logged in and has an existing PL application this ensures they can't start a new app.
const useRedirectIfExistingActivePLApp = ({ steps }) => {
  const history = useHistory();
  const match = useRouteMatch();
  const params = useParams();
  const { personalLoan } = usePersonalLoan(false);

  useEffect(() => {
    // It is not a uuid when starting a new flow, e.g. `/applications/start/-/basic-info/loan-amount` where the param uuid is still a `-`.
    if (!isUuid(params.uuid) && personalLoan?.uuid) {
      history.push(generatePath(`${match.path}/${steps[0].path}`, { uuid: personalLoan.uuid }));
    }
  }, [params.uuid, history, match.path, personalLoan.uuid, steps]);
};

const useInitialLoadOfProfileAndApplication = () => {
  const { isAuthenticated } = useIdentity();
  const { getProfile, profile } = useProfile(false);
  const { personalLoan, getActivePersonalLoan } = usePersonalLoan(false);
  const [isInitialized, setIsInitialized] = useState(profile.uuid && personalLoan.uuid);
  useEffect(() => {
    if (isAuthenticated && !isInitialized) {
      if (!profile.uuid || !personalLoan.uuid) {
        Promise.all([
          ...(!profile.uuid ? [getProfile()] : []),
          ...(!personalLoan.uuid ? [getActivePersonalLoan()] : []),
        ]).finally(() => setIsInitialized(true));
      }
    } else {
      setIsInitialized(true);
    }
  }, [
    getActivePersonalLoan,
    getProfile,
    isAuthenticated,
    isInitialized,
    personalLoan.uuid,
    profile.uuid,
  ]);

  return { isInitialized };
};

// Redirect apps to next spot if they have underwritten already to prevent downstream issues with updating data after uw
const useRedirectIfApplicationAlreadyStarted = ({ disableRedirect }) => {
  const runRouter = usePersonalLoanRouteFsm();
  const { uuid: appUuid } = useParams();
  const { isAuthenticated } = useIdentity();
  const { personalLoan } = usePersonalLoan(false);

  const uwDecision = personalLoan.status?.uwDecision || UNKNOWN;
  useEffect(() => {
    if (
      isAuthenticated &&
      !disableRedirect &&
      uwDecision !== UNKNOWN &&
      personalLoan?.status?.krangStatus !== KRANG_COMPLETE
    ) {
      runRouter(appUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uwDecision, appUuid, runRouter]);
};

// Redirect to not-yet-eligible page if less than 90 days (or less than 3 payments) for loans in progress
const useRedirectIfNotYetEligible = ({ disableRedirect }) => {
  const history = useHistory();
  const { uuid: appUuid } = useParams();
  const { personalLoan } = usePersonalLoan(false);

  useEffect(() => {
    if (!disableRedirect && appUuid && personalLoan?.status?.krangStatus === KRANG_COMPLETE) {
      history.push(`/applications/${appUuid}/not-yet-eligible`);
    }
  }, [appUuid, disableRedirect, history, personalLoan?.status?.krangStatus]);
};

export const BasicInfoAndCreateAccount = () => {
  const { telemetry } = useTelemetry();

  const runRouter = usePersonalLoanRouteFsm();
  const { redirectToDashboard } = useRedirect();
  const match = useRouteMatch();
  const { uuid: appUuid } = useParams();
  const { sendToWaitList } = useSendToWaitList();
  const rollbar = useRollbar();

  const { register: registerIdentity, isAuthenticated } = useIdentity();
  const { saveProfile, saveRegistrationConsents, savePhone, profile } = useProfile(false);
  const { getPersonalLoanConsentTypes, updatePersonalLoanv3 } = usePersonalLoan(false);
  const { userData: affiliatePrePopulatedData } = useRedux('affiliateReducer', affiliateActions);
  const { saveSmsOptInOut } = useSmsConsent(null, true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const { isInitialized: isProfileAndAppInitialized } = useInitialLoadOfProfileAndApplication({
    isSubmitting,
  });
  const {
    steps,
    validationSchema,
    initialValues,
    isInitialized: isStepsInitialized,
  } = useBasicInfoAndCreateAccountSteps();

  const isInitialized = isProfileAndAppInitialized && isStepsInitialized;

  useRedirectToFirstStepOnPageLoad({ steps });
  useRedirectIfExistingActivePLApp({ steps });
  useRedirectIfApplicationAlreadyStarted({ disableRedirect: isSubmitting });
  useRedirectIfNotYetEligible({ disableRedirect: isSubmitting });

  const handleOnSubmit = async (values) => {
    setIsSubmitting(true);
    if (!isAuthenticated) {
      try {
        await registerIdentity({ email: values.email, password: values.password });
      } catch (error) {
        if (
          error?.status === 409 &&
          error?.data?.errors.some((e) => e.match(/account already exists/i))
        ) {
          setFormError(
            'An account with that email already exists. Please login before continuing.'
          );
        }
        return error;
      }
      await saveRegistrationConsents();
      telemetry('plCreatedAccount');
    }
    try {
      const consents = await getPersonalLoanConsentTypes();

      const affiliateAppIndicator = affiliatePrePopulatedData?.affiliate?.length > 0 ?? false;
      const affiliateAbVersion = affiliatePrePopulatedData?.abVersion;
      try {
        await updatePersonalLoanv3({
          affiliateAppIndicator,
          ...(affiliateAbVersion ? { affiliateAbVersion } : {}),
          uuid: appUuid,
          applicationUuid: appUuid,
          reason: values.financingPurpose,
          currentLivingArrangement: values.livingArrangement,
          desiredLoanAmount: values.requestedAmount,
          consentsList: [
            {
              type: Consent.SOFT_CREDIT,
              version: consents.find((c) => c.type === Consent.SOFT_CREDIT).version,
              optInOut: 'OPT_IN',
            },
            {
              type: Consent.TRUEWORK,
              version: consents.find((c) => c.type === Consent.TRUEWORK).version,
              optInOut: 'OPT_IN',
            },
          ],
          income: {
            incomePrimary: values.totalIncome,
          },
          incomeSources: [{ plUuid: appUuid, employmentType: values.employmentType }],
          state: values.propertyAddress.state,
        });
      } catch (error) {
        if (error.data?.errors?.[0].includes('We do not currently offer loans')) {
          return sendToWaitList({
            name: {
              firstName: values.firstName || profile.name.firstName,
              lastName: values.lastName || profile.name.lastName,
            },
            email: values.email || profile.email,
          });
        } else {
          rollbar.error('Error saving personal loan application', error);
          throw error;
        }
      }

      if (isFigure) {
        await saveSmsOptInOut({
          optIn: values.callAndTextCommunicationConsent,
          phone: values.phone,
          email: profile.email || values.email,
        });
      }

      const homeAddress = (profile.addresses || []).reverse().find((a) => a.type === 'HOME') || {};
      try {
        await saveProfile({
          name: {
            dob: dateFormatter.formatISOLocalDate(values.dob),
            firstName: profile?.name?.firstName || values.firstName?.trim(),
            lastName: profile?.name?.lastName || values.lastName?.trim(),
            ...(!!values.suffix && { suffix: values.suffix }),
            ...(!!profile.name.suffix && { suffix: profile.name.suffix }),
          },
          homeAddress: {
            uuid: homeAddress.uuid || '',
            street1: values.propertyAddress.street1,
            street2: values.propertyAddress.street2,
            city: values.propertyAddress.city,
            state: values.propertyAddress.state,
            zip: values.propertyAddress.zip,
            type: 'HOME',
          },
          citizenship: values.citizenship,
        });
      } catch (pError) {
        rollbar.error('Error saving profile', pError, profile);
        throw pError;
      }

      await savePhone({ number: values.phone });
      telemetry('plStarted', {
        eventProperties: {
          loan_purpose: values.financingPurpose,
        },
      });

      return runRouter(appUuid);
    } catch (error) {
      rollbar.error('Error registering user', error, profile);
      redirectToDashboard();
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isInitialized && (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => handleOnSubmit(values)}
        >
          <Form>
            <Switch>
              {steps.map((step, currentStepIndex) => (
                <Route path={`${match.path}/${step.path}`} key={step.path}>
                  <Step
                    steps={steps}
                    step={step}
                    currentStepIndex={currentStepIndex}
                    match={match}
                    appUuid={appUuid}
                    formError={formError}
                  />
                </Route>
              ))}
            </Switch>
          </Form>
        </Formik>
      )}
    </>
  );
};
